import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  adminListBibles,
  adminCreateBible,
  adminUpdateBible,
  adminDeleteBible,
  adminListTools,
  fetchCategories,
  bibleImageUrl,
  api,
  adminHeaders,
} from "../lib/api";

// Fetch a draft-list preview PNG as a blob URL. We POST to the admin
// endpoint (POST because the body carries the unsaved draft), so an
// `<img src>` won't do — we hit it programmatically and turn the blob
// into an object URL the <img> can render. Object URLs are revoked
// when a new one is created or the component unmounts.
async function fetchDraftPreview(payload) {
  const res = await api.post("/admin/bibles/preview.png", payload, {
    headers: adminHeaders(),
    responseType: "blob",
    timeout: 30000,
  });
  return URL.createObjectURL(res.data);
}

// Filter a pool of tools to those whose TITLE (word-start match, so "AI"
// catches "AI Agents" / "AI-powered" but not the "ai" inside "email") OR
// whose URL/domain contains the keyword (substring, so "ai" catches
// "openai.com"). Case-insensitive. Empty keyword → pool unchanged.
function poolKeywordFilter(pool, keyword) {
  const kw = (keyword || "").trim().toLowerCase();
  if (!kw) return pool;
  const titleRe = new RegExp(
    "\\b" + kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    "i",
  );
  return pool.filter(
    (t) =>
      titleRe.test((t.title || "").toString()) ||
      (t.url || "").toString().toLowerCase().includes(kw),
  );
}

const emptyDraft = () => ({
  id: null,
  title: "untitled list",
  subtitle: "",
  footer_cta: "submit your tool \u2192 terminalvalue.io/submit",
  start_here: "",
  modules: [{ name: "module", tool_ids: [] }],
});

/**
 * Admin "+ lists" panel. Build a curated, module-grouped list of existing
 * tools and download it as a share image in the TerminalValue house style
 * (white, black mono, blue links, yellow highlight) for posting to X.
 * Entries reference approved tools already in the index.
 */
export default function AdminLists() {
  const [bibles, setBibles] = useState([]);
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryLabels, setDeliveryLabels] = useState({});
  const [deliveryGroups, setDeliveryGroups] = useState([]);
  const [draft, setDraft] = useState(emptyDraft());
  const [activeModule, setActiveModule] = useState(0);
  const [search, setSearch] = useState("");
  // Manual tool-picker filters — browse the add-list by function (category)
  // and/or asset class, not just free-text search.
  const [pickFunction, setPickFunction] = useState("");
  const [pickAsset, setPickAsset] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bust, setBust] = useState(0);
  // Live preview of the unsaved draft (object URL from /admin/bibles/preview.png).
  const [draftPreview, setDraftPreview] = useState("");
  const [draftPreviewLoading, setDraftPreviewLoading] = useState(false);
  // Combo selector state: filter by category, split by another axis, cap size.
  const [comboCategory, setComboCategory] = useState("");
  const [comboAxis, setComboAxis] = useState("asset_class");
  const [comboCap, setComboCap] = useState(75);
  // Optional keyword: restrict the pool to tools whose name/domain matches
  // (e.g. "AI" → AI-named tools + openai.com). Composes with category + cap.
  const [comboKeyword, setComboKeyword] = useState("");

  const toolById = useMemo(() => {
    const m = {};
    tools.forEach((t) => { m[t.id] = t; });
    return m;
  }, [tools]);

  // Derive dropdown options from the ACTUAL tool data, not the canonical
  // vocabulary endpoint. /api/categories includes labels like "Macro Data"
  // or "Jobs" that no tool is tagged with, so picking them would give
  // "no tools match". Showing counts (e.g. "research (36)") also makes
  // the picker self-documenting.
  const categoryOptions = useMemo(() => {
    const counts = {};
    tools.forEach((t) => {
      const k = (t.category || "").toString().trim();
      if (!k) return;
      counts[k] = (counts[k] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, n]) => ({ name, n }));
  }, [tools]);

  const assetClassOptions = useMemo(() => {
    const counts = {};
    tools.forEach((t) => {
      const k = (t.asset_class || "").toString().trim();
      if (!k) return;
      counts[k] = (counts[k] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, n]) => ({ name, n }));
  }, [tools]);

  const deliveryOptions = useMemo(() => {
    const counts = {};
    tools.forEach((t) => {
      (t.delivery || []).forEach((d) => {
        const k = (d || "").toString().trim();
        if (!k) return;
        counts[k] = (counts[k] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, n]) => ({ name, n }));
  }, [tools]);

  // Live count of tools matching the current category + keyword filter, so
  // the admin can see how many are available before they hit [ generate ].
  const comboPoolCount = useMemo(() => {
    const wantCat = (comboCategory || "").toLowerCase();
    const base = wantCat
      ? tools.filter((t) => (t.category || "").toString().toLowerCase() === wantCat)
      : tools;
    return poolKeywordFilter(base, comboKeyword).length;
  }, [tools, comboCategory, comboKeyword]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bl, tl, cats] = await Promise.all([
        adminListBibles(),
        adminListTools("approved"),
        fetchCategories(),
      ]);
      setBibles(bl);
      setTools(tl);
      setCategories(cats.categories || []);
      setAssetClasses(cats.asset_classes || []);
      setDeliveries(cats.delivery || []);
      setDeliveryLabels(cats.delivery_labels || {});
      setDeliveryGroups(cats.delivery_groups || []);
    } catch {
      toast.error("failed to load lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Unified balanced sampler. Takes a filter + an axis + a cap, and returns
  // sections where:
  //   - Each section is ranked internally by upvotes desc (best first).
  //   - Every section gets at least `minPerSection` tools (so small buckets
  //     like Crypto / Real Estate aren't starved off the share image).
  //   - The remaining cap is filled round-robin across sections, so bigger
  //     buckets naturally take more without erasing smaller ones.
  //   - Total tool count is bounded by `totalCap` (default 75) so the share
  //     image stays publishable, not an endless wall.
  //
  // For multi-valued axes (delivery), a tool can appear in multiple sections
  // because that's the meaningful representation — the cap is enforced on
  // unique tool IDs, not entries.
  const buildBalancedSplit = useCallback(
    (filterCategory, axis, totalCap = 75, minPerSection = 3, keyword = "") => {
      const wantCat = (filterCategory || "").toLowerCase();
      let pool = wantCat
        ? tools.filter((t) => (t.category || "").toString().toLowerCase() === wantCat)
        : tools;
      pool = poolKeywordFilter(pool, keyword);

      // Reverse map: delivery sub-type → friendly group key. Used when
      // splitting by delivery_group, and to label sections nicely when
      // splitting by raw delivery sub-type.
      const subToGroup = {};
      deliveryGroups.forEach((g) => {
        (g.subtypes || []).forEach((s) => { subToGroup[s.key] = g; });
      });

      // Group with full tool objects (need upvotes for ranking).
      const groups = {};
      const pushEach = (t, vals) => {
        const arr = vals && vals.length ? vals : ["(unspecified)"];
        arr.forEach((v) => {
          (groups[v] = groups[v] || []).push(t);
        });
      };
      pool.forEach((t) => {
        if (axis === "asset_class") pushEach(t, [(t.asset_class || "(unspecified)").toString()]);
        else if (axis === "delivery") {
          // Label sections with the friendly sub-type name (e.g. "MCP
          // Server" instead of "mcp").
          const labels = (t.delivery || []).map(
            (d) => (deliveryLabels[d] || d).toString(),
          );
          pushEach(t, labels);
        }
        else if (axis === "delivery_group") {
          // Bucket by parent group (Web / Installable / Agents & AI /
          // Programmatic / Assets). Multi-valued — a tool can belong to
          // multiple groups if it has sub-types from each.
          const groupLabels = Array.from(new Set(
            (t.delivery || [])
              .map((d) => subToGroup[d]?.label)
              .filter(Boolean),
          ));
          pushEach(t, groupLabels);
        }
        else if (axis === "category") pushEach(t, [(t.category || "(unspecified)").toString()]);
      });

      // Rank within each section: upvotes desc, title asc.
      Object.values(groups).forEach((arr) => {
        arr.sort(
          (a, b) =>
            (b.upvotes || 0) - (a.upvotes || 0) ||
            (a.title || "").localeCompare(b.title || ""),
        );
      });

      // Order sections by size desc (biggest first → shown prominently).
      const ordered = Object.keys(groups).sort(
        (a, b) => groups[b].length - groups[a].length || a.localeCompare(b),
      );

      const picks = ordered.map((name) => ({
        name,
        source: groups[name],
        cursor: 0,
        tool_ids: [],
      }));

      // Track unique tools (multi-valued delivery counts unique, not entries).
      const seenUnique = new Set();
      const tryAdd = (p) => {
        if (p.cursor >= p.source.length) return false;
        const t = p.source[p.cursor++];
        const isMultiValued = axis === "delivery" || axis === "delivery_group";
        if (isMultiValued && seenUnique.has(t.id)) {
          // Tool already picked via another section; still record it here
          // because the section presence is meaningful, but it doesn't
          // count against the cap.
          p.tool_ids.push(t.id);
          return true;
        }
        p.tool_ids.push(t.id);
        seenUnique.add(t.id);
        return true;
      };

      // Pass 1: guarantee minimum per section.
      picks.forEach((p) => {
        let added = 0;
        while (added < minPerSection && seenUnique.size < totalCap) {
          if (!tryAdd(p)) break;
          added += 1;
        }
      });

      // Pass 2: round-robin until cap on unique tools.
      let progressed = true;
      while (seenUnique.size < totalCap && progressed) {
        progressed = false;
        for (const p of picks) {
          if (seenUnique.size >= totalCap) break;
          if (p.cursor < p.source.length) {
            if (tryAdd(p)) progressed = true;
          }
        }
      }

      return picks
        .filter((p) => p.tool_ids.length > 0)
        .map((p) => ({ name: p.name, tool_ids: p.tool_ids }));
    },
    [tools, deliveryLabels, deliveryGroups],
  );

  // Single, deliberate generator. Reads the picker (filter + split axis)
  // and produces a draft list. No auto-triggers — the admin clicks
  // [ generate ] when they're ready.
  // Single, deliberate generator. Reads the picker (filter + split axis +
  // cap) and produces a draft list. No auto-triggers — the admin clicks
  // [ generate ] when they're ready. Every split path is sampled +
  // balanced through `buildBalancedSplit` so the result fits cleanly on
  // the share image and every section is represented.
  const generate = (opts = {}) => {
    const cat = comboCategory || "";
    const axis = comboAxis;
    const cap = Math.max(10, Math.min(150, parseInt(comboCap, 10) || 75));
    const kw = (comboKeyword || "").trim();

    const modules = buildBalancedSplit(cat, axis, cap, 3, kw);

    if (modules.length === 0) {
      if (!opts.silent) {
        toast.error(
          kw ? `no tools with "${kw}" in name/domain match that combo` : "no tools match that combo",
        );
      }
      return;
    }

    const total = modules.reduce((s, m) => s + m.tool_ids.length, 0);
    const uniqueIds = new Set();
    modules.forEach((m) => m.tool_ids.forEach((id) => uniqueIds.add(id)));
    const unique = uniqueIds.size;

    const axisLabel =
      axis === "asset_class" ? "asset class"
      : axis === "delivery_group" ? "delivery type"
      : axis === "delivery" ? "delivery"
      : axis;
    const kwLabel = kw ? `"${kw}" ` : "";
    const title = cat
      ? `${cat.toLowerCase()} ${kwLabel}tools by ${axisLabel}`
      : `${kwLabel}investing tools by ${axisLabel}`;
    const headline =
      unique === total
        ? `// ${total} tools across ${modules.length} sections`
        : `// ${unique} tools across ${modules.length} sections (${total} entries, multi-tagged)`;
    const filterNote = [
      cat ? cat.toLowerCase() : null,
      kw ? `name/domain ~ "${kw}"` : null,
    ].filter(Boolean).join(" · ");
    const subtitle = `${headline}${filterNote ? ` — ${filterNote} × ${axisLabel}` : ` — by ${axisLabel}`}, sampled`;

    setDraft({ ...emptyDraft(), title, subtitle, modules });
    setActiveModule(0);
    if (!opts.silent) {
      toast.success(
        unique === total
          ? `sampled ${total} tools across ${modules.length} sections (cap ${cap})`
          : `sampled ${unique} unique tools across ${modules.length} sections (${total} entries, cap ${cap})`,
      );
    }
  };

  const startNew = () => {
    setDraft(emptyDraft());
    setActiveModule(0);
  };

  const selectBible = (b) => {
    setDraft({
      id: b.id,
      title: b.title || "",
      subtitle: b.subtitle || "",
      footer_cta: b.footer_cta || "",
      start_here: b.start_here || "",
      modules: (b.modules && b.modules.length ? b.modules : [{ name: "module", tool_ids: [] }]).map((m) => ({
        name: m.name || "module",
        tool_ids: [...(m.tool_ids || [])],
      })),
    });
    setActiveModule(0);
    setBust(Date.now());
  };

  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // Debounced live preview: every time the draft changes (new generate,
  // module edit, tool add/remove), refetch the rendered PNG. Skips when
  // there's nothing to render. We use a 400ms debounce so rapid edits
  // don't spam the renderer.
  const previewTimer = useRef(null);
  const previewUrlRef = useRef("");
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    const hasContent = draft.modules.some((m) => m.tool_ids.length > 0);
    if (!hasContent) {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
      setDraftPreview("");
      return;
    }
    previewTimer.current = setTimeout(async () => {
      setDraftPreviewLoading(true);
      try {
        const payload = {
          title: draft.title || "untitled list",
          subtitle: draft.subtitle || null,
          modules: draft.modules
            .filter((m) => m.tool_ids.length > 0)
            .map((m) => ({ name: m.name, tool_ids: m.tool_ids })),
          footer_cta: draft.footer_cta || null,
          start_here: draft.start_here || null,
        };
        const url = await fetchDraftPreview(payload);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setDraftPreview(url);
      } catch {
        // Keep the old preview on transient errors — silent.
      } finally {
        setDraftPreviewLoading(false);
      }
    }, 400);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [draft.modules, draft.title, draft.subtitle, draft.footer_cta, draft.start_here]);

  // Revoke the blob URL on unmount.
  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const setModule = (idx, fn) =>
    setDraft((d) => ({ ...d, modules: d.modules.map((m, i) => (i === idx ? fn(m) : m)) }));

  const addModule = () =>
    setDraft((d) => ({ ...d, modules: [...d.modules, { name: "module", tool_ids: [] }] }));

  const removeModule = (idx) =>
    setDraft((d) => {
      const modules = d.modules.filter((_, i) => i !== idx);
      return { ...d, modules: modules.length ? modules : [{ name: "module", tool_ids: [] }] };
    });

  const moveModule = (idx, dir) =>
    setDraft((d) => {
      const j = idx + dir;
      if (j < 0 || j >= d.modules.length) return d;
      const modules = [...d.modules];
      [modules[idx], modules[j]] = [modules[j], modules[idx]];
      return { ...d, modules };
    });

  const addTool = (toolId) => {
    setModule(activeModule, (m) =>
      m.tool_ids.includes(toolId) ? m : { ...m, tool_ids: [...m.tool_ids, toolId] });
  };

  const removeTool = (idx, toolId) =>
    setModule(idx, (m) => ({ ...m, tool_ids: m.tool_ids.filter((t) => t !== toolId) }));

  const moveTool = (modIdx, pos, dir) =>
    setModule(modIdx, (m) => {
      const j = pos + dir;
      if (j < 0 || j >= m.tool_ids.length) return m;
      const ids = [...m.tool_ids];
      [ids[pos], ids[j]] = [ids[j], ids[pos]];
      return { ...m, tool_ids: ids };
    });

  const totalEntries = draft.modules.reduce((s, m) => s + m.tool_ids.length, 0);
  const uniqueEntries = useMemo(() => {
    const ids = new Set();
    draft.modules.forEach((m) => m.tool_ids.forEach((id) => ids.add(id)));
    return ids.size;
  }, [draft.modules]);

  const save = async () => {
    if (!draft.title.trim()) { toast.error("title required"); return; }
    if (totalEntries === 0) { toast.error("add at least one tool"); return; }
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        subtitle: draft.subtitle.trim(),
        footer_cta: draft.footer_cta.trim(),
        start_here: draft.start_here.trim(),
        modules: draft.modules.map((m) => ({ name: m.name.trim() || "module", tool_ids: m.tool_ids })),
      };
      const saved = draft.id
        ? await adminUpdateBible(draft.id, payload)
        : await adminCreateBible(payload);
      setDraft((d) => ({ ...d, id: saved.id }));
      setBust(Date.now());
      await load();
      toast.success(draft.id ? "list updated" : "list created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally {
      setSaving(false);
    }
  };

  // Save the current draft. Live preview already shows unsaved edits, so
  // we no longer need a separate "save + refresh" — save by itself is enough.
  const remove = async (id) => {
    if (!window.confirm("delete this list?")) return;
    try {
      await adminDeleteBible(id);
      if (draft.id === id) startNew();
      await load();
      toast.success("deleted");
    } catch {
      toast.error("delete failed");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(bibleImageUrl(draft.id));
    toast.success("image url copied");
  };

  const download = async () => {
    try {
      const res = await fetch(bibleImageUrl(draft.id, Date.now()));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(draft.title || "list").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("download failed");
    }
  };

  const filteredTools = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fn = pickFunction.toLowerCase();
    const ac = pickAsset.toLowerCase();
    return tools.filter((t) => {
      if (fn && (t.category || "").toString().toLowerCase() !== fn) return false;
      if (ac && (t.asset_class || "").toString().toLowerCase() !== ac) return false;
      if (!q) return true;
      return (
        (t.title || "").toLowerCase().includes(q) ||
        (t.asset_class || "").toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q)
      );
    });
  }, [tools, search, pickFunction, pickAsset]);

  return (
    <section data-testid="admin-lists" className="mt-4 border border-black p-4">
      <datalist id="bible-module-names">
        {[...categories, ...assetClasses].map((c) => <option key={c} value={c} />)}
      </datalist>
      <div className="text-[13px] font-bold mb-2">{">"} curated lists</div>
      <p className="text-[12px] text-[#666] mb-4">
        pick a filter, split axis, and tool cap. optionally restrict to tools
        with a keyword in their <span className="tv-hl">name/domain</span> (e.g. "AI").
        every list is{" "}
        <span className="tv-hl">sampled + balanced</span> — top tools per section, every section represented, capped at the size you set.
      </p>

      {/* single picker — deliberate generate, no auto-triggers */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-[12px]" data-testid="lists-combo">
        <span className="text-[#888]">filter:</span>
        <select
          value={comboCategory}
          onChange={(e) => setComboCategory(e.target.value)}
          className="border border-black bg-white px-1 py-0.5 font-mono text-[12px]"
          data-testid="combo-category-select"
        >
          <option value="">all categories ({tools.length})</option>
          {categoryOptions.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name.toLowerCase()} ({c.n})
            </option>
          ))}
        </select>
        <span className="text-[#888]">×</span>
        <span className="text-[#888]">split by:</span>
        <select
          value={comboAxis}
          onChange={(e) => setComboAxis(e.target.value)}
          className="border border-black bg-white px-1 py-0.5 font-mono text-[12px]"
          data-testid="combo-axis-select"
        >
          <option value="asset_class">asset class</option>
          <option value="delivery_group">delivery (5 groups: web / installable / agents & ai / programmatic / assets)</option>
          <option value="delivery">delivery (13 sub-types: web / mcp / prompt / ...)</option>
          <option value="category">category</option>
        </select>
        <span className="text-[#888]">cap:</span>
        <input
          type="number"
          min="10"
          max="150"
          step="5"
          value={comboCap}
          onChange={(e) => setComboCap(e.target.value)}
          className="border border-black bg-white px-1 py-0.5 font-mono text-[12px] w-14"
          data-testid="combo-cap-input"
        />
        <span className="text-[#888]">name/domain has:</span>
        <input
          type="text"
          value={comboKeyword}
          onChange={(e) => setComboKeyword(e.target.value)}
          placeholder="e.g. AI"
          className="border border-black bg-white px-1 py-0.5 font-mono text-[12px] w-20"
          data-testid="combo-keyword-input"
        />
        {comboKeyword.trim() && (
          <span className="text-[#888]" data-testid="combo-match-count">
            ({comboPoolCount} match)
          </span>
        )}
        <button className="tv-btn-primary" onClick={() => generate()} data-testid="combo-generate-btn">
          [ generate ]
        </button>
        <button className="tv-link" onClick={startNew} data-testid="list-new-btn">
          [ blank ]
        </button>
      </div>

      {/* section summary — visible immediately after [ generate ] so the
          admin can see what was created without scrolling through the editor */}
      {draft.modules.some((m) => m.tool_ids.length > 0) && (
        <div className="border border-[#ddd] bg-[#fafafa] px-3 py-2 mb-4 text-[12px]" data-testid="lists-summary">
          <div className="text-[#888] mb-1">
            <span className="text-black font-bold">{uniqueEntries}</span>
            {uniqueEntries !== totalEntries && (
              <> unique tools <span className="text-[#aaa]">({totalEntries} entries — tools may appear in multiple sections)</span></>
            )}
            {uniqueEntries === totalEntries && <> tools</>}
            {" across "}
            <span className="text-black font-bold">{draft.modules.filter((m) => m.tool_ids.length > 0).length}</span> sections:
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {draft.modules.map((m, mi) =>
              m.tool_ids.length > 0 ? (
                <span key={mi} className="text-[12px]" data-testid={`summary-section-${mi}`}>
                  <span className="tv-hl">{m.name}</span>{" "}
                  <span className="text-[#666]">({m.tool_ids.length})</span>
                </span>
              ) : null,
            )}
          </div>
        </div>
      )}

      {/* existing lists */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-[12px]" data-testid="lists-existing">
        <span className="text-[#888]">saved lists:</span>
        {loading ? (
          <span className="text-[#888]">loading...</span>
        ) : bibles.length === 0 ? (
          <span className="text-[#bbb]">none yet — organize + save to create one</span>
        ) : (
          bibles.map((b) => (
            <span key={b.id} className="inline-flex items-center gap-1 border border-[#ccc] px-2 py-1">
              <button
                className={`tv-link ${draft.id === b.id ? "font-bold tv-hl" : ""}`}
                onClick={() => selectBible(b)}
                data-testid={`list-open-${b.id}`}
              >
                {b.title}
              </button>
              <button
                className="tv-link text-red-700"
                onClick={() => remove(b.id)}
                data-testid={`list-delete-${b.id}`}
                title="delete"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* editor */}
        <div data-testid="list-editor" className="order-2 lg:order-1">
          <div className="text-[12px] font-bold mb-2">{draft.id ? "edit list" : "new list"}</div>

          <label className="block text-[11px] text-[#888] mb-1">
            list name <span className="text-[#bbb]">(your reference only — used for the file name, not shown on the image)</span>
          </label>
          <input
            className="tv-input w-full mb-3"
            value={draft.title}
            onChange={(e) => patch("title", e.target.value)}
            maxLength={80}
            data-testid="list-title-input"
          />

          <label className="block text-[11px] text-[#888] mb-1">
            subtitle <span className="text-[#bbb]">(blank = auto "// N tools across M modules")</span>
          </label>
          <input
            className="tv-input w-full mb-3"
            value={draft.subtitle}
            onChange={(e) => patch("subtitle", e.target.value)}
            maxLength={160}
            placeholder="// hand-picked by TerminalValue"
            data-testid="list-subtitle-input"
          />

          <label className="block text-[11px] text-[#888] mb-1">
            start-here line <span className="text-[#bbb]">(optional yellow highlight)</span>
          </label>
          <input
            className="tv-input w-full mb-3"
            value={draft.start_here}
            onChange={(e) => patch("start_here", e.target.value)}
            maxLength={120}
            placeholder="Koyfin — the all-in-one terminal"
            data-testid="list-starthere-input"
          />

          <label className="block text-[11px] text-[#888] mb-1">footer cta</label>
          <input
            className="tv-input w-full mb-4"
            value={draft.footer_cta}
            onChange={(e) => patch("footer_cta", e.target.value)}
            maxLength={120}
            data-testid="list-footer-input"
          />

          {/* modules */}
          <div className="text-[12px] font-bold mb-2">
            modules <span className="text-[#888] font-normal">({totalEntries} tools)</span>
          </div>
          <div className="space-y-3" data-testid="list-modules">
            {draft.modules.map((m, mi) => (
              <div
                key={mi}
                className={`border p-2 ${activeModule === mi ? "border-black" : "border-[#ddd]"}`}
                data-testid={`list-module-${mi}`}
              >
                <div className="flex items-center gap-1 mb-2">
                  <span className="text-[11px] text-[#888]">## module</span>
                  <input
                    className="tv-input flex-1 text-[12px]"
                    value={m.name}
                    onChange={(e) => setModule(mi, (mm) => ({ ...mm, name: e.target.value }))}
                    maxLength={40}
                    list="bible-module-names"
                    data-testid={`list-module-name-${mi}`}
                  />
                  <button className="tv-link" onClick={() => moveModule(mi, -1)} title="move up" data-testid={`list-module-up-${mi}`}>↑</button>
                  <button className="tv-link" onClick={() => moveModule(mi, 1)} title="move down" data-testid={`list-module-down-${mi}`}>↓</button>
                  <button className="tv-link text-red-700" onClick={() => removeModule(mi)} title="remove module" data-testid={`list-module-remove-${mi}`}>×</button>
                </div>
                {m.tool_ids.length === 0 ? (
                  <div className="text-[11px] text-[#bbb] pl-2">
                    {activeModule === mi ? "pick tools below ↓" : "empty — click [+ add to] then pick tools"}
                  </div>
                ) : (
                  <ol className="space-y-1">
                    {m.tool_ids.map((tid, pos) => (
                      <li key={tid} className="flex items-center gap-2 text-[12px] pl-1" data-testid={`list-entry-${mi}-${tid}`}>
                        <span className="text-[#888] w-5">{String(pos + 1).padStart(2, "0")}</span>
                        <span className="flex-1 truncate">{toolById[tid]?.title || <span className="text-red-700">missing tool</span>}</span>
                        <button className="tv-link" onClick={() => moveTool(mi, pos, -1)} title="up">↑</button>
                        <button className="tv-link" onClick={() => moveTool(mi, pos, 1)} title="down">↓</button>
                        <button className="tv-link text-red-700" onClick={() => removeTool(mi, tid)} title="remove">×</button>
                      </li>
                    ))}
                  </ol>
                )}
                {activeModule !== mi && (
                  <button
                    className="tv-link text-[11px] mt-2"
                    onClick={() => setActiveModule(mi)}
                    data-testid={`list-module-activate-${mi}`}
                  >
                    [+ add tools to this module]
                  </button>
                )}
              </div>
            ))}
          </div>
          <button className="tv-link text-[12px] mt-2" onClick={addModule} data-testid="list-add-module-btn">
            [+ add module]
          </button>

          {/* tool picker */}
          <div className="mt-4 border-t border-[#ddd] pt-3">
            <div className="text-[11px] text-[#888] mb-2">
              adding to: <span className="tv-hl">{draft.modules[activeModule]?.name || "module"}</span>
            </div>
            {/* browse the pool by function (category) and/or asset class —
                not just free-text search. */}
            <div className="flex flex-wrap items-center gap-2 mb-2 text-[11px]" data-testid="picker-filters">
              <span className="text-[#888]">function:</span>
              <select
                value={pickFunction}
                onChange={(e) => setPickFunction(e.target.value)}
                className="border border-black bg-white px-1 py-0.5 font-mono text-[11px]"
                data-testid="picker-function-select"
              >
                <option value="">all ({tools.length})</option>
                {categoryOptions.map((c) => (
                  <option key={c.name} value={c.name}>{c.name.toLowerCase()} ({c.n})</option>
                ))}
              </select>
              <span className="text-[#888]">asset:</span>
              <select
                value={pickAsset}
                onChange={(e) => setPickAsset(e.target.value)}
                className="border border-black bg-white px-1 py-0.5 font-mono text-[11px]"
                data-testid="picker-asset-select"
              >
                <option value="">all</option>
                {assetClassOptions.map((a) => (
                  <option key={a.name} value={a.name}>{a.name.toLowerCase()} ({a.n})</option>
                ))}
              </select>
              {(pickFunction || pickAsset) && (
                <button
                  className="tv-link"
                  onClick={() => { setPickFunction(""); setPickAsset(""); }}
                  data-testid="picker-clear-filters"
                >
                  [clear]
                </button>
              )}
            </div>
            <input
              className="tv-input w-full mb-2 text-[12px]"
              placeholder="search tools..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="list-tool-search"
            />
            <div className="max-h-[220px] overflow-y-auto border border-[#eee]" data-testid="list-tool-picker">
              {filteredTools.map((t) => {
                const inModule = draft.modules[activeModule]?.tool_ids.includes(t.id);
                return (
                  <button
                    key={t.id}
                    className={`block w-full text-left px-2 py-1 text-[12px] border-b border-[#f0f0f0] hover:bg-[#fafafa] ${inModule ? "text-[#bbb]" : ""}`}
                    onClick={() => addTool(t.id)}
                    disabled={inModule}
                    data-testid={`list-pick-${t.id}`}
                  >
                    {inModule ? "✓ " : "+ "}{t.title}
                    <span className="text-[#aaa]">
                      {" · "}{t.category || "—"}
                      {t.asset_class ? <span className="text-[#ccc]"> · {t.asset_class}</span> : null}
                    </span>
                  </button>
                );
              })}
              {filteredTools.length === 0 && (
                <div className="text-[11px] text-[#bbb] p-2">no tools match</div>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button className="tv-btn-primary" onClick={save} disabled={saving} data-testid="list-save-btn">
              {saving ? "[ saving... ]" : draft.id ? "[ save changes ]" : "[ create list ]"}
            </button>
          </div>
        </div>

        {/* preview */}
        <div data-testid="list-preview" className="order-1 lg:order-2">
          <div className="text-[12px] font-bold mb-2">
            share image preview
            {draftPreviewLoading && (
              <span className="text-[#888] font-normal text-[11px] ml-2">rendering<span className="tv-blink">_</span></span>
            )}
          </div>
          {draft.modules.some((m) => m.tool_ids.length > 0) ? (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-2 text-[12px]">
                {draft.id && (
                  <>
                    <button className="tv-link" onClick={download} data-testid="list-download-btn">[ download png ]</button>
                    <button
                      className="tv-link"
                      onClick={async () => {
                        const url = `${window.location.origin}/list/${draft.id}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          toast.success(`copied: ${url}`);
                        } catch {
                          toast.error("clipboard not available");
                        }
                      }}
                      data-testid="list-copy-share-url"
                    >
                      [ copy share url ]
                    </button>
                    <a
                      className="tv-link"
                      href={`/list/${draft.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="list-view-public"
                    >
                      [ open public page ↗ ]
                    </a>
                  </>
                )}
                {!draft.id && (
                  <span className="text-[#888]">save to get a shareable URL</span>
                )}
              </div>
              <div className="border border-black bg-white p-1">
                {draftPreview ? (
                  <img
                    src={draftPreview}
                    alt="list share preview"
                    className="w-full block"
                    data-testid="list-preview-img"
                  />
                ) : draft.id ? (
                  <img
                    src={bibleImageUrl(draft.id, bust || 1)}
                    alt="list share preview"
                    className="w-full block"
                    data-testid="list-preview-img"
                  />
                ) : (
                  <div className="text-[12px] text-[#888] p-6 text-center">
                    rendering preview<span className="tv-blink">_</span>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-[#888] mt-2">
                preview updates live as you edit. click <span className="tv-hl">[ create list ]</span> to save and get a shareable URL.
              </p>
            </>
          ) : (
            <div className="text-[12px] text-[#888] border border-[#ddd] p-6 text-center" data-testid="list-preview-empty">
              click [ generate ] to create a list, then the share image will appear here.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
